import React from "react";
import {
  AlertCircle,
  BookOpen,
  Bug,
  TrendingUp,
  Zap,
  AlertTriangle,
  Bell,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Circle,
  ExternalLink,
  Play,
  Search,
  SlidersHorizontal,
  CircleCheck,
  Clock,
  Compass,
  Eye,
  EyeOff,
  FileText,
  Globe,
  Heart,
  Home,
  Image as ImageIconLucide,
  Inbox,
  LayoutGrid,
  Link2,
  ListChecks,
  Lock,
  Ticket,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Monitor,
  Package,
  Pencil,
  Crop,
  RotateCcw,
  RotateCw,
  Pause,
  ArrowLeftRight,
  Languages,
  Copy,
  CornerDownLeft,
  SunMedium,
  Pin,
  PinOff,
  Folder,
  FolderPlus,
  Gift,
  KeyRound,
  LogOut,
  PlayCircle,
  Plus,
  RefreshCw,
  Settings,
  Sparkles,
  Send,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldCheck,
  Target,
  Trash2,
  UserX,
  Users,
  Tag,
  ChevronLeft,
  Star,
  Upload,
  User,
  X
} from "lucide-react";
import { A } from "../theme/palette";

const renderLucideIcon = (IconComponent, { size = 20, color = "currentColor", strokeWidth = 2, ...rest }) => (
  <IconComponent size={size} color={color} strokeWidth={strokeWidth} {...rest} />
);

// Имя экспорта оставлено прежним: под ним знак импортируют App, AuthPortal и
// ProxyChecker, и переименование ради самого переименования тронуло бы три файла
// без единого изменения по существу.
//
// Круглая обрезка снята — у нового знака своя скруглённая плитка, и обрезать её
// по окружности значило бы срезать углы, которые к ней и относятся.
//
// Знак рисуется здесь, а не картинкой: в assets/umbrella-logo.svg цвет плитки
// вписан (индиго), и в красной теме логотип оставался синим. Контур — тот же
// путь из того файла; цвет — акцент текущей темы.
const UMBRELLA_LOGO_PATH = "M16.55 0H47.45A16.55 16.55 0 0 1 64 16.55V47.45A16.55 16.55 0 0 1 47.45 64H16.55A16.55 16.55 0 0 1 0 47.45V16.55A16.55 16.55 0 0 1 16.55 0ZM14.35 34.76a17.65 17.65 0 0 1 35.3 0zM30.62 34.76H33.38V46.9H30.62Z";

export const ArmorLogoIcon = ({ size = 28 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 64 64"
    width={size}
    height={size}
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", width: `${size}px`, height: `${size}px`, flexShrink: 0 }}
  >
    <path fill={A[500]} fillRule="evenodd" d={UMBRELLA_LOGO_PATH} />
  </svg>
);

export const DashboardIcon = (props) => renderLucideIcon(LayoutGrid, props);
export const UserIcon = (props) => renderLucideIcon(User, props);
export const LinkIcon = (props) => renderLucideIcon(Link2, props);
export const MessageIcon = (props) => renderLucideIcon(MessageSquare, props);
export const SparklesIcon = (props) => renderLucideIcon(Sparkles, props);
export const EyeIcon = (props) => renderLucideIcon(Eye, props);
export const EyeOffIcon = (props) => renderLucideIcon(EyeOff, props);
export const HeartIcon = (props) => renderLucideIcon(Heart, props);
export const PlusIcon = (props) => renderLucideIcon(Plus, props);
export const RefreshIcon = (props) => renderLucideIcon(RefreshCw, props);
export const EditIcon = (props) => renderLucideIcon(Pencil, props);
export const CropIcon = (props) => renderLucideIcon(Crop, props);
export const RotateLeftIcon = (props) => renderLucideIcon(RotateCcw, props);
export const RotateRightIcon = (props) => renderLucideIcon(RotateCw, props);
export const BrightnessIcon = (props) => renderLucideIcon(SunMedium, props);
export const PauseIcon = (props) => renderLucideIcon(Pause, props);
export const SwapIcon = (props) => renderLucideIcon(ArrowLeftRight, props);
export const LanguagesIcon = (props) => renderLucideIcon(Languages, props);
export const CopyIcon = (props) => renderLucideIcon(Copy, props);
export const CornerDownLeftIcon = (props) => renderLucideIcon(CornerDownLeft, props);
export const FileIcon = (props) => renderLucideIcon(FileText, props);
export const CheckIcon = (props) => renderLucideIcon(Check, props);
export const XIcon = (props) => renderLucideIcon(X, props);
export const TrashIcon = (props) => renderLucideIcon(Trash2, props);
// Deleting an ad out of everyone's feed, as opposed to one's own bin.
export const UsersSlashIcon = (props) => renderLucideIcon(UserX, props);
export const UsersIcon = (props) => renderLucideIcon(Users, props);
export const TagIcon = (props) => renderLucideIcon(Tag, props);
export const ChevronLeftIcon = (props) => renderLucideIcon(ChevronLeft, props);
export const StarIcon = (props) => renderLucideIcon(Star, props);
export const ChevronRightIcon = (props) => renderLucideIcon(ChevronRight, props);
export const AlertIcon = (props) => renderLucideIcon(AlertCircle, props);
export const AlertTriangleIcon = (props) => renderLucideIcon(AlertTriangle, props);
export const SuccessIcon = (props) => renderLucideIcon(CircleCheck, props);

// One spinner for every "busy" state in the panel.
//
// The hand-rolled version was a bordered div with border-radius: 50%, and inside
// a flex button without flex-shrink: 0 it got squeezed horizontally — a 16px
// circle collapsing to a couple of pixels wide, which spun as a thin diagonal
// streak that read as a rotating pencil. Drawn as SVG with the shrink pinned, it
// stays a circle wherever it is placed.
export const Spinner = ({ size = 16, color = "currentColor", trackOpacity = 0.35, strokeWidth = 2.5, style, ...rest }) => (
  <svg
    className="kl-busy-spinner"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0, display: "block", ...style }}
    {...rest}
  >
    <circle cx="12" cy="12" r="9" stroke={color} strokeOpacity={trackOpacity} strokeWidth={strokeWidth} />
    <path d="M21 12a9 9 0 0 0-9-9" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
  </svg>
);

export const LoadingIcon = ({ className = "", ...rest }) => {
  const nextClassName = ["animate-spin", className].filter(Boolean).join(" ");
  return renderLucideIcon(Loader2, { ...rest, className: nextClassName });
};

export const StatusOnlineIcon = ({ size = 20, color = "#22c55e", strokeWidth = 1.5, ...rest }) => (
  <Circle size={size} color={color} fill={color} strokeWidth={strokeWidth} {...rest} />
);

export const TargetIcon = (props) => renderLucideIcon(Target, props);
export const PackageIcon = (props) => renderLucideIcon(Package, props);
export const SendIcon = (props) => renderLucideIcon(Send, props);
export const PanelLeftCloseIcon = (props) => renderLucideIcon(PanelLeftClose, props);
export const PanelLeftOpenIcon = (props) => renderLucideIcon(PanelLeftOpen, props);
export const BellIcon = (props) => renderLucideIcon(Bell, props);
export const MailIcon = (props) => renderLucideIcon(Mail, props);
export const GlobeIcon = (props) => renderLucideIcon(Globe, props);
export const MapPinIcon = (props) => renderLucideIcon(MapPin, props);
export const BuildingIcon = (props) => renderLucideIcon(Building2, props);
export const PinIcon = (props) => renderLucideIcon(Pin, props);
export const PinOffIcon = (props) => renderLucideIcon(PinOff, props);
export const FolderIcon = (props) => renderLucideIcon(Folder, props);
export const FolderPlusIcon = (props) => renderLucideIcon(FolderPlus, props);
export const HomeIcon = (props) => renderLucideIcon(Home, props);
export const CompassIcon = (props) => renderLucideIcon(Compass, props);
export const MonitorIcon = (props) => renderLucideIcon(Monitor, props);
export const ImageIcon = (props) => renderLucideIcon(ImageIconLucide, props);
export const UploadIcon = (props) => renderLucideIcon(Upload, props);
export const PlayCircleIcon = (props) => renderLucideIcon(PlayCircle, props);
export const ClockIcon = (props) => renderLucideIcon(Clock, props);
export const SettingsIcon = (props) => renderLucideIcon(Settings, props);
export const ShieldCheckIcon = (props) => renderLucideIcon(ShieldCheck, props);
export const InboxIcon = (props) => renderLucideIcon(Inbox, props);
export const ListChecksIcon = (props) => renderLucideIcon(ListChecks, props);
export const SearchIcon = (props) => renderLucideIcon(Search, props);
export const PlayIcon = (props) => renderLucideIcon(Play, props);
export const SlidersIcon = (props) => renderLucideIcon(SlidersHorizontal, props);
export const ChevronUpIcon = (props) => renderLucideIcon(ChevronUp, props);
export const ChevronDownIcon = (props) => renderLucideIcon(ChevronDown, props);
export const ExternalLinkIcon = (props) => renderLucideIcon(ExternalLink, props);
export const LockIcon = (props) => renderLucideIcon(Lock, props);
export const TicketIcon = (props) => renderLucideIcon(Ticket, props);
export const GiftIcon = (props) => renderLucideIcon(Gift, props);
export const KeyIcon = (props) => renderLucideIcon(KeyRound, props);
export const LogOutIcon = (props) => renderLucideIcon(LogOut, props);
export const TrendingUpIcon = (props) => renderLucideIcon(TrendingUp, props);
export const ZapIcon = (props) => renderLucideIcon(Zap, props);
export const BookOpenIcon = (props) => renderLucideIcon(BookOpen, props);
export const BugIcon = (props) => renderLucideIcon(Bug, props);
